import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Grid} from "@mui/system";
import style from "../../../assets/css/proposal.module.scss";
import GlobalTextField from "../../global-fields/GlobalTextField";
import {useFormik} from "formik";
import * as Yup from "yup";
import {MANDATORY_QUES} from "../../../constants/constants";
import {useDispatch, useSelector} from "react-redux";
import {selectAuthState, setAuthData} from "../../../store/authSlice";
import {selectDataStoreState, setDataStore} from "../../../store/dataSlice";
import {DIAMOND_FORM} from "../../../constants/apiConstant";
import {
  areRequiredFieldsFilled,
  initialFormValidations,
  parseToNumber,
  removeCookies,
  USD_FORMATTER,
} from "../../../constants/helper";
import GlobalNotificationPopup from "../../global-fields/GlobalNotificationPopup";
import {setUiData} from "../../../store/uiSlice";
import {useNavigate} from "react-router-dom";
import {motion} from "framer-motion";
import {fadeSlideIn} from "../../../constants/motionConstants";
import {
  amountSchemaWithRequired,
  numberSchemaWithPercentage,
} from "../../../constants/validationSchemas";
import GlobalYearMonthField from "../../global-fields/GlobalYearMonthField";
import dayjs from "dayjs";
import GlobalMultiTextField from "../../global-fields/GlobalMultiTextField";
import {IoIosClose} from "react-icons/io";
import diamondStyle from "../../../assets/css/diamond-form.module.scss";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import styled from "styled-components";

import {handleFileUploadWithoutVids} from "../../../constants/helper";
import GlobalCheckBox from "../../global-fields/GlobalCheckBox";

import plusIcon from "../../../assets/img/proposal-img/plus_icon.svg";
import GlobalTimePickerField from "../../global-fields/GlobalTimePickerField";
import GlobalErrorMsgBox from "../../global-fields/GlobalErrorMsgBox";
import GlobalDateField from "../../global-fields/GlobalDateField";
import GlobalPhoneInput from "../../global-fields/GlobalPhoneInput";

const MAX_FINISHED_STONES = 10; // Limit to 20 values

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const DiamondForm = ({leftGridWidth}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {user, selectedClient} = useSelector(selectAuthState);
  const {
    proposal_id,
    proposalStep,
    diamondFormData,
    previous_proposal_id,
    editingSubmitted,
    formValidations,
  } = useSelector(selectDataStoreState);
  const declaration1Ref = useRef();
  const declaration2Ref = useRef();
  const declaration1SignRef = useRef();
  const declaration2SignRef = useRef();
  const sketchRef = useRef();

  const handleDiamondFormData = (e) => {
    dispatch(
      setUiData({
        showLoader: true,
      })
    );

    let data = {
      ...e,
      user_id: user?.id,
      declaration_1_date: e.declaration_1_date
        ? dayjs(e.declaration_1_date).format("MM-DD-YYYY")
        : "",
      declaration_2_date: e.declaration_2_date
        ? dayjs(e.declaration_2_date).format("MM-DD-YYYY")
        : "",
    };

    if (user?.user_type === "agent") {
      data = {...data, user_id: selectedClient?.user_id, agent_id: user?.id};
    }

    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (
        [
          "finished_stones_details",
          "sketch",
          "declaration_1_sign",
          "declaration_2_sign",
          "processing_time",
          "process_undertaken",
        ].includes(key)
      ) {
        return;
      }
      formData.append(key, value ?? "");
    });

    if (Array.isArray(data?.finished_stones_details)) {
      data.finished_stones_details.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]) => {
          formData.append(
            `finished_stones_details[${index}][${key}]`,
            key === "estimated_weight"
              ? value
                ? value.toString()
                : "0"
              : (value ?? "")
          );
        });
      });
    }

    data.process_undertaken?.forEach((value, index) => {
      formData.append(`process_undertaken[${index}]`, value ?? "");
    });

    formData.append(
      "processing_time",
      data.processing_time ? dayjs(data.processing_time).format("HH:mm") : ""
    );

    ["sketch", "declaration_1_sign", "declaration_2_sign"].forEach(
      (fileKey) => {
        const file = data[fileKey];
        if (file instanceof File) {
          formData.append(fileKey, file);
        } else {
          formData.append(fileKey, ""); // append empty if no file selected
        }
      }
    );

    const options = {
      method: DIAMOND_FORM.method,
      body: formData,
    };

    fetch(DIAMOND_FORM.url, options)
      .then((response) => response.json())
      .then((res) => {
        if (res.status == 0) {
          dispatch(
            setUiData({
              showLoader: false,
            })
          );

          handleReset();
        } else if (res.status == 1) {
          handleReset();
          dispatch(
            setDataStore({
              proposalStep: 0,
            })
          );
          dispatch(
            setUiData({
              showLoader: false,
            })
          );
          navigate(`/dashboard`);
        }
      })
      .catch((error) => {
        removeCookies("isLogin");
        GlobalNotificationPopup({type: "error", message: error});
        console.log("error", error);
        dispatch(
          setUiData({
            showLoader: false,
          })
        );
        navigate("/");
        handleReset();
      });
  };

  const diamondFormDataMemoize = useMemo(
    () => (diamondFormData ? diamondFormData : null),
    [diamondFormData]
  );
  const initialValues = {
    name:
      diamondFormDataMemoize?.name ??
      (user?.user_type !== "agent" ? user?.company : selectedClient?.company) ??
      null,
    phone_number:
      diamondFormDataMemoize?.phone_number ??
      (user?.user_type !== "agent"
        ? user?.phone_number
        : selectedClient?.mobile_no) ??
      null,
    email:
      diamondFormDataMemoize?.email ??
      (user?.user_type !== "agent" ? user?.email : selectedClient?.email) ??
      null,
    street_owner:
      diamondFormDataMemoize?.street_owner ??
      (user?.user_type !== "agent"
        ? user?.address1
        : selectedClient?.address1) ??
      null,
    suite_owner:
      diamondFormDataMemoize?.suite_owner ??
      (user?.user_type !== "agent"
        ? user?.address2
        : selectedClient?.address2) ??
      null,
    city_owner:
      diamondFormDataMemoize?.city_owner ??
      (user?.user_type !== "agent" ? user?.city : selectedClient?.city) ??
      null,
    state_owner:
      diamondFormDataMemoize?.state_owner ??
      (user?.user_type !== "agent" ? user?.state : selectedClient?.state) ??
      null,
    zip_code_owner:
      diamondFormDataMemoize?.zip_code_owner ??
      (user?.user_type !== "agent" ? user?.zipcode : selectedClient?.zipcode) ??
      null,
    mine_origin: diamondFormDataMemoize?.mine_origin ?? "",
    price_to_owner: diamondFormDataMemoize?.price_to_owner ?? "",
    weight:
      diamondFormDataMemoize?.weight == 0
        ? "0"
        : diamondFormDataMemoize?.weight
          ? diamondFormDataMemoize?.weight.toString()
          : null,
    dimensions: diamondFormDataMemoize?.dimensions ?? "",
    general_shape: diamondFormDataMemoize?.general_shape ?? "",
    color: diamondFormDataMemoize?.color ?? "",
    imperfetion: diamondFormDataMemoize?.imperfetion ?? "",
    fluorescence: diamondFormDataMemoize?.fluorescence ?? "",
    skin: diamondFormDataMemoize?.skin ?? "",
    sketch: diamondFormDataMemoize?.sketch ?? null, // File input
    processing_time: diamondFormDataMemoize?.processing_time
      ? dayjs(diamondFormDataMemoize?.processing_time, "HH:mm")
      : "",
    ind_processor: diamondFormDataMemoize?.ind_processor ?? "",
    processing_company: diamondFormDataMemoize?.processing_company ?? "",
    street_company:
      diamondFormDataMemoize?.street_company ??
      (user?.user_type !== "agent"
        ? user?.address1
        : selectedClient?.address1) ??
      null,
    suite_company:
      diamondFormDataMemoize?.suite_company ??
      (user?.user_type !== "agent"
        ? user?.address2
        : selectedClient?.address2) ??
      null,
    city_company:
      diamondFormDataMemoize?.city_company ??
      (user?.user_type !== "agent" ? user?.city : selectedClient?.city) ??
      null,
    state_company:
      diamondFormDataMemoize?.state_company ??
      (user?.user_type !== "agent" ? user?.state : selectedClient?.state) ??
      null,
    zip_code_company:
      diamondFormDataMemoize?.zip_code_company ??
      (user?.user_type !== "agent" ? user?.zipcode : selectedClient?.zipcode) ??
      null,
    process_undertaken:
      diamondFormDataMemoize?.process_undertaken?.length > 0
        ? diamondFormDataMemoize?.process_undertaken
        : [],
    process_undertaken_other:
      diamondFormDataMemoize?.process_undertaken_other ?? "",
    estimated_loss:
      diamondFormDataMemoize?.estimated_loss == 0
        ? "0"
        : diamondFormDataMemoize?.estimated_loss
          ? diamondFormDataMemoize?.estimated_loss.toString()
          : null,
    finished_stones_details:
      diamondFormDataMemoize?.finished_stones_details?.length > 0
        ? diamondFormDataMemoize.finished_stones_details.map((item) => ({
            ...item,
            estimated_weight:
              item?.estimated_weight == 0
                ? "0"
                : item?.estimated_weight
                  ? item?.estimated_weight.toString()
                  : null,
            shape_cut: item?.shape_cut ?? "",
            clarity: item?.clarity ?? "",
            color: item?.color ?? "",
          }))
        : [
            {
              estimated_weight: "",
              shape_cut: "",
              clarity: "",
              color: "",
            },
          ],
    finished_stones_comments:
      diamondFormDataMemoize?.finished_stones_comments ?? "",
    hazards_involved_comments:
      diamondFormDataMemoize?.hazards_involved_comments ?? "",
    declaration_1: diamondFormDataMemoize?.declaration_1 ?? false,
    declaration_1_name: diamondFormDataMemoize?.declaration_1_name ?? "",
    declaration_1_date: diamondFormDataMemoize?.declaration_1_date ?? "",
    declaration_1_sign: diamondFormDataMemoize?.declaration_1_sign ?? null, // File input
    declaration_2: diamondFormDataMemoize?.declaration_2 ?? false,
    declaration_2_name: diamondFormDataMemoize?.declaration_2_name ?? "",
    declaration_2_date: diamondFormDataMemoize?.declaration_2_date ?? "",
    declaration_2_sign: diamondFormDataMemoize?.declaration_2_sign ?? null, // File input
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .required(MANDATORY_QUES)
        .matches(
          /^(?! )[A-Za-z0-9.\-&,\/\s]*[A-Za-z.\-&,\/][A-Za-z0-9.\-&,\/\s]*$/,
          "Firm name may include letters, numbers, periods, hyphens, ampersands, commas, slashes, and spaces—but must not start with a space."
        ),

      // phone_number: Yup.string()
      //     .required(MANDATORY_QUES)
      //     .matches(/^[0-9]{7,15}$/, "Enter a valid phone number."),
      phone_number: Yup.string()
        .required("Mobile Number is required!")
        .matches(/^[+()\-.\s\d]+$/, "Enter a valid mobile number."),
      email: Yup.string()
        .email("Enter a valid email address!")
        .required("Email is required!"),
      street_owner: Yup.string().required(MANDATORY_QUES),
      suite_owner: Yup.string().notRequired(),
      city_owner: Yup.string()
        .required(MANDATORY_QUES)
        .matches(/^[A-Za-z\s]+$/, "Enter a valid city name (alphabets only)."),
      state_owner: Yup.string()
        .required(MANDATORY_QUES)
        .matches(/^[A-Za-z\s]+$/, "Enter a valid state name (alphabets only)."),
      zip_code_owner: Yup.string()
        .required(MANDATORY_QUES)
        .matches(
          /^\d{5}(-\d{4})?$/,
          "Enter a valid US zip code (e.g., 12345 or 12345-6789)."
        ),
      mine_origin: Yup.string().required(MANDATORY_QUES),
      price_to_owner: amountSchemaWithRequired,
      weight: Yup.string()
        .typeError("Must be a number")
        .min(0, "Value must be positive.")
        .required(MANDATORY_QUES)
        .matches(/^\d+(\.\d+)?$/, "Enter a valid number for estimated weight."),
      dimensions: Yup.string().required(MANDATORY_QUES),
      general_shape: Yup.string().required(MANDATORY_QUES),
      color: Yup.string().required(MANDATORY_QUES),
      imperfetion: Yup.string().required(MANDATORY_QUES),
      fluorescence: Yup.string().required(MANDATORY_QUES),
      skin: Yup.string().required(MANDATORY_QUES),
      sketch: Yup.mixed()
        .nullable()
        .test("fileSize", "File size too large", (value) =>
          value ? value.size <= 5 * 1024 * 1024 : true
        )
        .test("fileType", "Unsupported file format", (value) =>
          value
            ? ["image/jpeg", "image/png", "application/pdf"].includes(
                value.type
              )
            : true
        )
        .required(MANDATORY_QUES),
      processing_time: Yup.string().required(MANDATORY_QUES),
      ind_processor: Yup.string().required(MANDATORY_QUES),
      processing_company: Yup.string()
        .required(MANDATORY_QUES)
        .matches(
          /^(?! )[A-Za-z0-9.\-&,\/\s]*[A-Za-z.\-&,\/][A-Za-z0-9.\-&,\/\s]*$/,
          "Company name may include letters, numbers, periods, hyphens, ampersands, commas, slashes, and spaces—but must not start with a space."
        ),
      street_company: Yup.string().required(MANDATORY_QUES),
      suite_company: Yup.string().notRequired(),
      city_company: Yup.string()
        .required(MANDATORY_QUES)
        .matches(/^[A-Za-z\s]+$/, "Enter a valid city name (alphabets only)."),
      state_company: Yup.string()
        .required(MANDATORY_QUES)
        .matches(/^[A-Za-z\s]+$/, "Enter a valid state name (alphabets only)."),
      zip_code_company: Yup.string()
        .required(MANDATORY_QUES)
        .matches(
          /^\d{5}(-\d{4})?$/,
          "Enter a valid US zip code (e.g., 12345 or 12345-6789)."
        ),
      process_undertaken: Yup.array()
        .of(Yup.string())
        .min(1, MANDATORY_QUES)
        .required(MANDATORY_QUES),
      process_undertaken_other: Yup.string().when("process_undertaken", {
        is: (arr) => arr?.includes("Other"),
        then: (schema) => schema.required("Please specify other process."),
        otherwise: (schema) => schema.notRequired(),
      }),
      estimated_loss: Yup.string()
        .typeError("Must be a number")
        .min(0, "Value must be positive.")
        .required(MANDATORY_QUES)
        .matches(/^\d+(\.\d+)?$/, "Enter a valid number for estimated weight."),
      finished_stones_details: Yup.array()
        .of(
          Yup.object().shape({
            estimated_weight: Yup.string()
              .typeError("Must be a number")
              .min(0, "Value must be positive.")
              .required(MANDATORY_QUES)
              .matches(
                /^\d+(\.\d+)?$/,
                "Enter a valid number for estimated weight."
              ),
            shape_cut: Yup.string().required(MANDATORY_QUES),
            clarity: Yup.string().required(MANDATORY_QUES),
            color: Yup.string().required(MANDATORY_QUES),
          })
        )
        .min(1, "At least one finished stone detail is required."),
      finished_stones_comments: Yup.string().notRequired(),
      hazards_involved_comments: Yup.string().required(MANDATORY_QUES),
      declaration_1: Yup.boolean()
        .oneOf([true], "You must accept Declaration 1")
        .required(MANDATORY_QUES),
      declaration_1_name: Yup.string().when("declaration_1", {
        is: true || false,
        then: (schema) => schema.required(MANDATORY_QUES),
      }),
      declaration_1_date: Yup.string().when("declaration_1", {
        is: true || false,
        then: (schema) => schema.required(MANDATORY_QUES),
      }),
      declaration_1_sign: Yup.mixed()
        .nullable()
        .when("declaration_1", {
          is: true || false,
          then: (schema) => schema.required(MANDATORY_QUES),
        }),
      declaration_2: Yup.boolean().oneOf(
        [true],
        "You must accept Declaration 2"
      ),
      declaration_2_name: Yup.string().when("declaration_2", {
        is: true || false,
        then: (schema) => schema.required(MANDATORY_QUES),
      }),
      declaration_2_date: Yup.string().when("declaration_2", {
        is: true || false,
        then: (schema) => schema.required(MANDATORY_QUES),
      }),
      declaration_2_sign: Yup.mixed()
        .nullable()
        .when("declaration_2", {
          is: true || false,
          then: (schema) => schema.required(MANDATORY_QUES),
        }),
    }),
    onSubmit: (values) => {
      // if (false) {
      //console.log("Form submitted 1:", values);
      handleDiamondFormData(values);
      // } else {
      //     GlobalNotificationPopup({
      //         type: "error",
      //         message:
      //             "Total percentage of Manufacturing, Retail, Wholesale, and Pawnbroking together can't exceed or less than 100%",
      //     });
      // }
    },
  });

  // console.log("diamondFormDataMemoize", diamondFormDataMemoize, initialValues)
  // console.log("formik", formik.values, formik.errors);

  const handleAmountFieldChangeold = (e, fieldName) => {
    const rawValue = e.target.value;
    const numericValue = parseToNumber(rawValue);
    formik.setFieldValue(
      fieldName,
      numericValue !== "" ? USD_FORMATTER.format(numericValue) : ""
    );
  };

  const handleAmountFieldChange = (e, fieldName) => {
    let rawValue = e.target.value;

    // Always ensure it starts with $
    if (!rawValue.startsWith("$")) rawValue = "$" + rawValue;

    // Extract the numeric part
    const numericValue = parseToNumber(rawValue);

    // If user just typed "$" or "$.", don't break their typing
    if (rawValue === "$" || rawValue === "$.") {
      formik.setFieldValue(fieldName, rawValue);
      return;
    }

    // Format only when numericValue is valid
    if (numericValue !== "") {
      const [intPart, decimalPart] = rawValue
        .replace(/[^0-9.]/g, "")
        .split(".");

      let formatted = "$" + USD_FORMATTER.format(intPart);
      if (decimalPart !== undefined) formatted += "." + decimalPart;

      formik.setFieldValue(fieldName, formatted);
    } else {
      formik.setFieldValue(fieldName, "");
    }
  };

  const undertakenOptions = [
    "Cleaving",
    "Sawing",
    "Cutting",
    "Bruting",
    "Girdling",
    "Polishing",
    "Other",
  ];

  const handleClickUndertakenOptions = (option) => {
    const current = formik.values.process_undertaken || [];
    let updated = [...current];

    if (option === "Other") {
      // Selecting "Other" clears all others
      updated = updated.includes("Other") ? [] : ["Other"];
    } else {
      // Remove "Other" if it exists
      updated = updated.filter((item) => item !== "Other");

      if (updated.includes(option)) {
        // Uncheck
        updated = updated.filter((item) => item !== option);
      } else {
        // Check
        updated.push(option);
      }
    }

    formik.setFieldValue("process_undertaken", updated);
  };

  const handlePlusFinishedStones = useCallback(() => {
    const finishedStones = formik.values.finished_stones_details;
    const lastStone = finishedStones[finishedStones.length - 1];

    // Validate last entry fields before adding a new one
    if (
      !lastStone.estimated_weight ||
      !lastStone.shape_cut ||
      !lastStone.clarity ||
      !lastStone.color
    ) {
      GlobalNotificationPopup({
        type: "warning",
        message:
          "Please fill all fields of the last finished stone before adding a new entry.",
      });
      return;
    }

    // Optional: limit maximum number of finished stones
    if (finishedStones.length >= MAX_FINISHED_STONES) return;

    // Add a new blank finished stone
    formik.setFieldValue("finished_stones_details", [
      ...finishedStones,
      {
        estimated_weight: "",
        shape_cut: "",
        clarity: "",
        color: "",
      },
    ]);
  }, [formik.values.finished_stones_details]);

  const handleReset = () => {
    // Reset Formik form values
    formik.resetForm();

    // Reset declaration checkboxes
    if (declaration1Ref.current) {
      declaration1Ref.current.checked = false;
      formik.setFieldValue("declaration_1", false);
    }
    if (declaration2Ref.current) {
      declaration2Ref.current.checked = false;
      formik.setFieldValue("declaration_2", false);
    }

    // Reset file inputs
    if (declaration1SignRef.current) {
      declaration1SignRef.current.value = null;
      formik.setFieldValue("declaration_1_sign", null);
    }
    if (declaration2SignRef.current) {
      declaration2SignRef.current.value = null;
      formik.setFieldValue("declaration_2_sign", null);
    }
    if (sketchRef.current) {
      sketchRef.current.value = null;
      formik.setFieldValue("sketch", null);
    }
  };

  const handleMinusFinishedStones = useCallback(
    (index) => {
      if (formik.values.finished_stones_details.length === 1) return;

      const newfinished_stones_details = [
        ...formik.values.finished_stones_details,
      ];
      newfinished_stones_details.splice(index, 1);
      formik.setFieldValue(
        "finished_stones_details",
        newfinished_stones_details
      );
    },
    [formik.values.finished_stones_details]
  );

  return (
    <>
      <section className={diamondStyle.DiamondMain}>
        <motion.div {...fadeSlideIn}>
          <Box className={diamondStyle.DiamondMainBox}>
            {selectedClient !== null ? (
              <div className={style.client_title}>
                <p> On behalf of Client : </p>
                <Stack
                  sx={{pb: 2, pt: 1}}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <h3>
                    {selectedClient?.first_name} {selectedClient?.last_name}
                  </h3>
                  {/* {proposal_id ? null : ( */}
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => {
                      navigate("/clients");
                      dispatch(
                        setAuthData({
                          selectedClient: null,
                        })
                      );
                    }}
                  >
                    <IoIosClose />
                  </Button>
                  {/* )}    */}
                </Stack>
                <Divider />
              </div>
            ) : null}
            <Grid container spacing={2}>
              <Grid size={{xs: 12}}>
                <div className={diamondStyle.diamondMTitle}>
                  <h1>Certificate of Examination Diamond/s to be Processed</h1>
                </div>
              </Grid>
            </Grid>
            <form
              onSubmit={formik.handleSubmit}
              autoComplete="off"
              autoSave="off"
            >
              <Grid container spacing={2}>
                <Grid size={{xs: 12}}>
                  <h2 className={style.formTitleh2}>
                    A. Details for Owner of the Stone
                  </h2>
                </Grid>
                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Name *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="name"
                        name="name"
                        error={
                          formik.touched.name && Boolean(formik.errors.name)
                        }
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Phone Number *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      {/* <GlobalTextField
                                                type="text"
                                                value={formik.values.phone_number}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                id="phone_number"
                                                name="phone_number"
                                                error={
                                                    formik.touched.phone_number &&
                                                    Boolean(formik.errors.phone_number)
                                                }
                                                helperText={
                                                    formik.touched.phone_number &&
                                                    formik.errors.phone_number
                                                }
                                            /> */}
                      <GlobalPhoneInput
                        id="phone_number"
                        name="phone_number"
                        value={formik.values.phone_number}
                        onChange={({value, countryDetails, formattedVal}) => {
                          //const value = e.target.value.replace(/[^0-9]/g, "");
                          formik.setFieldValue("phone_number", formattedVal);
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.phone_number &&
                          Boolean(formik.errors.phone_number)
                        }
                        helperText={
                          formik.touched.phone_number &&
                          formik.errors.phone_number
                        }
                        // cssClass={!enable1Edit ? "phoneinputDiabledClass" : ""}
                        // disabled={!enable1Edit}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Email Address *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        id="email"
                        name="email"
                        type="email"
                        placeH="Enter your email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}> Address of the Owner</h2>
                </Grid>

                <Grid size={{xs: 12, md: 6, sm: 6, xl: 6}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Street *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.street_owner}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="street_owner"
                        name="street_owner"
                        error={
                          formik.touched.street_owner &&
                          Boolean(formik.errors.street_owner)
                        }
                        helperText={
                          formik.touched.street_owner &&
                          formik.errors.street_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 6, sm: 6, md: 6, xl: 6}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>
                        Suite / Apartment / Unit #
                      </h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.suite_owner}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="suite_owner"
                        name="suite_owner"
                        error={
                          formik.touched.suite_owner &&
                          Boolean(formik.errors.suite_owner)
                        }
                        helperText={
                          formik.touched.suite_owner &&
                          formik.errors.suite_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>State *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.state_owner}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="state_owner"
                        name="state_owner"
                        error={
                          formik.touched.state_owner &&
                          Boolean(formik.errors.state_owner)
                        }
                        helperText={
                          formik.touched.state_owner &&
                          formik.errors.state_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>City *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.city_owner}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="city_owner"
                        name="city_owner"
                        error={
                          formik.touched.city_owner &&
                          Boolean(formik.errors.city_owner)
                        }
                        helperText={
                          formik.touched.city_owner && formik.errors.city_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Zip Code *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.zip_code_owner}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="zip_code_owner"
                        name="zip_code_owner"
                        error={
                          formik.touched.zip_code_owner &&
                          Boolean(formik.errors.zip_code_owner)
                        }
                        helperText={
                          formik.touched.zip_code_owner &&
                          formik.errors.zip_code_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}>B. Details of the Stone</h2>
                </Grid>

                <Grid size={{xs: 12, xl: 6, md: 6, sm: 6, lg: 6, xl: 6}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Mine of Origin *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="mine_origin"
                        name="mine_origin"
                        value={formik.values?.mine_origin}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.mine_origin &&
                          Boolean(formik.errors?.mine_origin)
                        }
                        helperText={
                          formik.touched?.mine_origin &&
                          formik.errors?.mine_origin
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 6, md: 6, sm: 6, lg: 6, xl: 6}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>
                        Cost Price to Owner *
                      </h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        placeH="$"
                        type="text"
                        name={`price_to_owner`}
                        id={`price_to_owner`}
                        value={formik.values?.price_to_owner}
                        onChange={(e) =>
                          handleAmountFieldChange(e, "price_to_owner")
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.price_to_owner &&
                          Boolean(formik.errors?.price_to_owner)
                        }
                        helperText={
                          formik.touched?.price_to_owner &&
                          formik.errors?.price_to_owner
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}>
                    C. Examiner’s Valuation of Unprocessed Stone
                  </h2>
                </Grid>

                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Weight (carats) *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="string"
                        id="weight"
                        name="weight"
                        value={formik.values?.weight}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.weight &&
                          Boolean(formik.errors?.weight)
                        }
                        helperText={
                          formik.touched?.weight && formik.errors?.weight
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Dimensions *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="dimensions"
                        name="dimensions"
                        value={formik.values?.dimensions}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.dimensions &&
                          Boolean(formik.errors?.dimensions)
                        }
                        helperText={
                          formik.touched?.dimensions &&
                          formik.errors?.dimensions
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>General Shape *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="general_shape"
                        name="general_shape"
                        value={formik.values?.general_shape}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.general_shape &&
                          Boolean(formik.errors?.general_shape)
                        }
                        helperText={
                          formik.touched?.general_shape &&
                          formik.errors?.general_shape
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Color *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="color"
                        name="color"
                        value={formik.values?.color}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.color && Boolean(formik.errors?.color)
                        }
                        helperText={
                          formik.touched?.color && formik.errors?.color
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Imperfection *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="imperfetion"
                        name="imperfetion"
                        value={formik.values?.imperfetion}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.imperfetion &&
                          Boolean(formik.errors?.imperfetion)
                        }
                        helperText={
                          formik.touched?.imperfetion &&
                          formik.errors?.imperfetion
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Fluorescence *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="fluorescence"
                        name="fluorescence"
                        value={formik.values?.fluorescence}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.fluorescence &&
                          Boolean(formik.errors?.fluorescence)
                        }
                        helperText={
                          formik.touched?.fluorescence &&
                          formik.errors?.fluorescence
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Skin *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        id="skin"
                        name="skin"
                        value={formik.values?.skin}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.skin && Boolean(formik.errors?.skin)
                        }
                        helperText={formik.touched?.skin && formik.errors?.skin}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}>
                    D. Sketch of Stone (showing any imperfection and areas to be
                    processed)
                  </h2>
                </Grid>

                <Grid size={{xs: 12, md: 12}} className="choosefileBtn">
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                  >
                    {formik.values?.sketch?.name ? (
                      <span>{formik.values?.sketch?.name}</span>
                    ) : formik.values?.sketch?.includes("https://") ? (
                      <>
                        <span className={style.safeVFile}>
                          {formik.values?.sketch.split("/images/")[1]}
                        </span>
                      </>
                    ) : (
                      "Upload"
                    )}
                    <VisuallyHiddenInput
                      ref={sketchRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={(event) =>
                        handleFileUploadWithoutVids({
                          event,
                          fieldName: "sketch",
                          formik,
                          onError: (message) =>
                            GlobalNotificationPopup({
                              type: "error",
                              message,
                            }),
                        })
                      }
                    />
                  </Button>
                  {formik.touched?.sketch && formik?.errors?.sketch ? (
                    <GlobalErrorMsgBox errMessage={formik?.errors?.sketch} />
                  ) : null}
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}> E. Processing Details</h2>
                </Grid>

                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>Processing Time *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTimePickerField
                        title={"Select Time"}
                        id={`processing_time`}
                        name={`processing_time`}
                        value={formik.values?.processing_time}
                        onChange={(newValue) =>
                          formik.setFieldValue(`processing_time`, newValue)
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched?.processing_time &&
                          Boolean(formik.errors?.processing_time)
                        }
                        helperText={
                          formik.touched?.processing_time &&
                          formik.errors?.processing_time
                        }
                        clearBtn={formik.values?.processing_time !== ""}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>
                        Name of Individual Processor *
                      </h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.ind_processor}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="ind_processor"
                        name="ind_processor"
                        error={
                          formik.touched.ind_processor &&
                          Boolean(formik.errors.ind_processor)
                        }
                        helperText={
                          formik.touched.ind_processor &&
                          formik.errors.ind_processor
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{xs: 12, xl: 4, sm: 6, md: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={1}>
                    <Grid size={{xs: 12, md: 12}} sx={{mb: 1}}>
                      <h3 className={style.htitleText}>
                        Name of Processing Company *
                      </h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.processing_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="processing_company"
                        name="processing_company"
                        error={
                          formik.touched.processing_company &&
                          Boolean(formik.errors.processing_company)
                        }
                        helperText={
                          formik.touched.processing_company &&
                          formik.errors.processing_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}>
                    Address of Processing Company
                  </h2>
                </Grid>

                <Grid size={{xs: 12, md: 6, sm: 6, xl: 6}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Street *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.street_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="street_company"
                        name="street_company"
                        error={
                          formik.touched.street_company &&
                          Boolean(formik.errors.street_company)
                        }
                        helperText={
                          formik.touched.street_company &&
                          formik.errors.street_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, xl: 6, sm: 6, md: 6, xl: 6}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>
                        Apartment/Suite/Floor
                      </h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.suite_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="suite_company"
                        name="suite_company"
                        error={
                          formik.touched.suite_company &&
                          Boolean(formik.errors.suite_company)
                        }
                        helperText={
                          formik.touched.suite_company &&
                          formik.errors.suite_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>State *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.state_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="state_company"
                        name="state_company"
                        error={
                          formik.touched.state_company &&
                          Boolean(formik.errors.state_company)
                        }
                        helperText={
                          formik.touched.state_company &&
                          formik.errors.state_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>City *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.city_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="city_company"
                        name="city_company"
                        error={
                          formik.touched.city_company &&
                          Boolean(formik.errors.city_company)
                        }
                        helperText={
                          formik.touched.city_company &&
                          formik.errors.city_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Zip Code *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.zip_code_company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="zip_code_company"
                        name="zip_code_company"
                        error={
                          formik.touched.zip_code_company &&
                          Boolean(formik.errors.zip_code_company)
                        }
                        helperText={
                          formik.touched.zip_code_company &&
                          formik.errors.zip_code_company
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12}} sx={{mt: 1}}>
                  <h2 className={style.formTitleh2}>
                    Processes to be Undertaken
                  </h2>
                </Grid>

                <Grid size={{xs: 12}}>
                  <div
                    className={
                      diamondStyle.diamondCheckMain + " " + "diamond_checkBox"
                    }
                  >
                    {undertakenOptions.map((opt) => (
                      <GlobalCheckBox
                        key={opt}
                        title={opt}
                        selected={formik.values.process_undertaken?.includes(
                          opt
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          handleClickUndertakenOptions(opt);
                        }}
                      />
                    ))}

                    {/* 
                                        <GlobalCheckBox selected={true} title="Cleaving" />
                                        <GlobalCheckBox title="Sawing" />
                                        <GlobalCheckBox title="Cutting" />
                                        <GlobalCheckBox title="Bruting" />
                                        <GlobalCheckBox selected={true} title="Girdling" />
                                        <GlobalCheckBox title="Polishing" />
                                        <GlobalCheckBox title="Other" /> */}
                  </div>
                  {formik.touched?.process_undertaken &&
                  formik?.errors?.process_undertaken ? (
                    <GlobalErrorMsgBox
                      errMessage={formik?.errors?.process_undertaken}
                    />
                  ) : null}
                </Grid>

                <Grid size={{xs: 12, md: 12}}>
                  <h3 className={style.htitleText} style={{fontWeight: "300"}}>
                    If “Other” (Please Specify)
                  </h3>
                </Grid>
                <Grid size={{xs: 12, md: 12}}>
                  <GlobalTextField
                    type="text"
                    value={formik.values.process_undertaken_other}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="process_undertaken_other"
                    name="process_undertaken_other"
                    error={
                      formik.touched.process_undertaken_other &&
                      Boolean(formik.errors.process_undertaken_other)
                    }
                    helperText={
                      formik.touched.process_undertaken_other &&
                      formik.errors.process_undertaken_other
                    }
                  />
                </Grid>

                <Grid size={{xs: 12, md: 12}}>
                  <h3 className={style.htitleText}>
                    Estimated weight loss from above process. *
                  </h3>
                </Grid>
                <Grid size={{xs: 12, md: 12}}>
                  <GlobalTextField
                    type="text"
                    value={formik.values.estimated_loss}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    id="estimated_loss"
                    name="estimated_loss"
                    error={
                      formik.touched.estimated_loss &&
                      Boolean(formik.errors.estimated_loss)
                    }
                    helperText={
                      formik.touched.estimated_loss &&
                      formik.errors.estimated_loss
                    }
                  />
                </Grid>

                <Grid size={{xs: 12, md: 12}} sx={{mt: 0}}>
                  <div className={style.plusIconMain}>
                    <div>
                      <h2 className={style.formTitleh2}>
                        Number of finished stones with details
                      </h2>
                      <p style={{marginLeft: "0px", marginTop: "10px"}}>
                        If more than one, please give full details and estimated
                        weight.
                      </p>
                    </div>
                    <div
                      className={style.plusImgMain + " " + style.plusImgWidth}
                    >
                      {/* Add Icon - Show only at index 0 and if the limit is not exceeded */}
                      {formik.values.finished_stones_details.length <
                        MAX_FINISHED_STONES && (
                        <p
                          style={{
                            position: "inherit",
                            display: "flex",
                          }}
                          onClick={handlePlusFinishedStones}
                        >
                          <img
                            src={plusIcon}
                            alt="Add"
                            style={{marginRight: "5px"}}
                          />
                          Add
                        </p>
                      )}
                    </div>
                  </div>
                </Grid>
                {formik.values.finished_stones_details.map((fsd, index) => {
                  return (
                    <>
                      {index !== 0 && (
                        <Box sx={{width: "100%", mt: 1, mb: 0}}>
                          <Divider sx={{position: "relative"}}>
                            <Chip
                              label={` ${index + 1}`}
                              size="medium"
                              clickable
                              onDelete={() => handleMinusFinishedStones(index)}
                            />
                          </Divider>
                        </Box>
                      )}

                      <Grid size={{xs: 12, md: 3, sm: 6, lg: 3, xl: 3}}>
                        <Grid container spacing={2}>
                          <Grid size={{xs: 12, md: 12}}>
                            <h3 className={style.htitleText}>
                              Estimated weight (ct) *
                            </h3>
                          </Grid>
                          <Grid size={{xs: 12, md: 12}}>
                            <GlobalTextField
                              type="text"
                              name={`finished_stones_details[${index}].estimated_weight`}
                              labelId={`finished_stones_details${index}_estimated_weight`}
                              value={
                                formik.values?.finished_stones_details?.[index]
                                  ?.estimated_weight || ""
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `finished_stones_details.[${index}].estimated_weight`,
                                  e.target.value
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.estimated_weight &&
                                Boolean(
                                  formik.errors?.finished_stones_details?.[
                                    index
                                  ]?.estimated_weight
                                )
                              }
                              helperText={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.estimated_weight &&
                                formik.errors?.finished_stones_details?.[index]
                                  ?.estimated_weight
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={{xs: 12, md: 3, sm: 6, lg: 3, xl: 3}}>
                        <Grid container spacing={2}>
                          <Grid size={{xs: 12, md: 12}}>
                            <h3 className={style.htitleText}>Shape/cut *</h3>
                          </Grid>
                          <Grid size={{xs: 12, md: 12}}>
                            <GlobalTextField
                              type="text"
                              name={`finished_stones_details[${index}].shape_cut`}
                              labelId={`finished_stones_details${index}_shape_cut`}
                              value={
                                formik.values?.finished_stones_details?.[index]
                                  ?.shape_cut || ""
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `finished_stones_details.[${index}].shape_cut`,
                                  e.target.value
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.shape_cut &&
                                Boolean(
                                  formik.errors?.finished_stones_details?.[
                                    index
                                  ]?.shape_cut
                                )
                              }
                              helperText={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.shape_cut &&
                                formik.errors?.finished_stones_details?.[index]
                                  ?.shape_cut
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={{xs: 12, md: 3, sm: 6, lg: 3, xl: 3}}>
                        <Grid container spacing={2}>
                          <Grid size={{xs: 12, md: 12}}>
                            <h3 className={style.htitleText}>Clarity *</h3>
                          </Grid>
                          <Grid size={{xs: 12, md: 12}}>
                            <GlobalTextField
                              type="text"
                              name={`finished_stones_details[${index}].clarity`}
                              labelId={`finished_stones_details${index}_clarity`}
                              value={
                                formik.values?.finished_stones_details?.[index]
                                  ?.clarity || ""
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `finished_stones_details.[${index}].clarity`,
                                  e.target.value
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.clarity &&
                                Boolean(
                                  formik.errors?.finished_stones_details?.[
                                    index
                                  ]?.clarity
                                )
                              }
                              helperText={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.clarity &&
                                formik.errors?.finished_stones_details?.[index]
                                  ?.clarity
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid size={{xs: 12, md: 3, sm: 6, lg: 3, xl: 3}}>
                        <Grid container spacing={2}>
                          <Grid size={{xs: 12, md: 12}}>
                            <h3 className={style.htitleText}>Color *</h3>
                          </Grid>
                          <Grid size={{xs: 12, md: 12}}>
                            <GlobalTextField
                              //type="text"
                              // value={formik.values.city}
                              // onChange={formik.handleChange}
                              // onBlur={formik.handleBlur}
                              // id="city"
                              // name="city"
                              // error={
                              //     formik.touched.city && Boolean(formik.errors.city)
                              // }
                              // helperText={formik.touched.city && formik.errors.city}

                              type="text"
                              name={`finished_stones_details[${index}].color`}
                              labelId={`finished_stones_details${index}_color`}
                              value={
                                formik.values?.finished_stones_details?.[index]
                                  ?.color || ""
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `finished_stones_details.[${index}].color`,
                                  e.target.value
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.color &&
                                Boolean(
                                  formik.errors?.finished_stones_details?.[
                                    index
                                  ]?.color
                                )
                              }
                              helperText={
                                formik.touched?.finished_stones_details?.[index]
                                  ?.color &&
                                formik.errors?.finished_stones_details?.[index]
                                  ?.color
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  );
                })}

                <Grid size={{xs: 12, md: 12, sm: 12, lg: 12, xl: 12}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Comments, If any</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.finished_stones_comments}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="finished_stones_comments"
                        name="finished_stones_comments"
                        error={
                          formik.touched.finished_stones_comments &&
                          Boolean(formik.errors.finished_stones_comments)
                        }
                        helperText={
                          formik.touched.finished_stones_comments &&
                          formik.errors.finished_stones_comments
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 12, sm: 12, lg: 12, xl: 12}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <div
                        className={style.plusIconMain}
                        style={{display: "block"}}
                      >
                        <h2 className={style.formTitleh2}>
                          Comments relating to any undue hazards involved
                        </h2>
                        <p style={{marginLeft: "0px", marginTop: "10px"}}>
                          Please give comments relating to any undue hazards
                          involved in carrying out the specified processing
                        </p>
                      </div>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.hazards_involved_comments}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="hazards_involved_comments"
                        name="hazards_involved_comments"
                        error={
                          formik.touched.hazards_involved_comments &&
                          Boolean(formik.errors.hazards_involved_comments)
                        }
                        helperText={
                          formik.touched.hazards_involved_comments &&
                          formik.errors.hazards_involved_comments
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 12}} className="diamondCheckMain">
                  <h2 className={style.formTitleh2}> F. Declaration </h2>

                  <div
                    className={`${style.log_checkbox} ${style.RegisterCheckbox} `}
                  >
                    <FormControlLabel
                      sx={{width: "auto", alignItems: "flex-start", mt: 1}}
                      className={style.LogRemember}
                      control={
                        <Checkbox
                          checked={formik.values?.declaration_1}
                          onChange={(e) => {
                            formik.setFieldValue(
                              `declaration_1`,
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label="I certify that I have examined the above mentioned stone and that in my opinion there are no undue risks in carrying out the processes specified above"
                    />
                  </div>
                  {formik.touched?.declaration_1 &&
                  formik?.errors?.declaration_1 ? (
                    <GlobalErrorMsgBox
                      errMessage={formik?.errors?.declaration_1}
                    />
                  ) : null}
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Name *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.declaration_1_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="declaration_1_name"
                        name="declaration_1_name"
                        error={
                          formik.touched.declaration_1_name &&
                          Boolean(formik.errors.declaration_1_name)
                        }
                        helperText={
                          formik.touched.declaration_1_name &&
                          formik.errors.declaration_1_name
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Date *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalDateField
                        value={
                          formik.values.declaration_1_date
                            ? dayjs(
                                formik.values.declaration_1_date,
                                "MM-DD-YYYY"
                              ).toDate()
                            : null
                        }
                        onChange={(newValue) => {
                          formik.setFieldValue(`declaration_1_date`, newValue);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched(`declaration_1_date`, true)
                        }
                        id="declaration_1_date"
                        name="declaration_1_date"
                        error={
                          formik.touched.declaration_1_date &&
                          Boolean(formik.errors.declaration_1_date)
                        }
                        helperText={
                          formik.touched.declaration_1_date &&
                          formik.errors.declaration_1_date
                        }
                        clearBtn={formik.values.declaration_1_date !== ""}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Signature *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}} className="choosefileBtn">
                      <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        startIcon={<CloudUploadIcon />}
                      >
                        {formik.values?.declaration_1_sign?.name ? (
                          <span>{formik.values?.declaration_1_sign?.name}</span>
                        ) : formik.values?.declaration_1_sign?.includes(
                            "https://"
                          ) ? (
                          <>
                            <span className={style.safeVFile}>
                              {
                                formik.values?.declaration_1_sign.split(
                                  "/images/"
                                )[1]
                              }
                            </span>
                          </>
                        ) : (
                          "Upload"
                        )}
                        <VisuallyHiddenInput
                          ref={declaration1SignRef}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                          onChange={(event) =>
                            handleFileUploadWithoutVids({
                              event,
                              fieldName: `declaration_1_sign`,
                              formik,
                              onError: (message) =>
                                GlobalNotificationPopup({
                                  type: "error",
                                  message,
                                }),
                            })
                          }
                        />
                      </Button>
                      {formik.touched?.declaration_1_sign &&
                      formik?.errors?.declaration_1_sign ? (
                        <GlobalErrorMsgBox
                          errMessage={formik?.errors?.declaration_1_sign}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 12}} className="diamondCheckMain">
                  <h2 className={style.formTitleh2}> F. Declaration </h2>

                  <div
                    className={`${style.log_checkbox} ${style.RegisterCheckbox} `}
                  >
                    <FormControlLabel
                      sx={{width: "auto", alignItems: "flex-start", mt: 1}}
                      className={style.LogRemember}
                      control={
                        <Checkbox
                          checked={formik.values?.declaration_2}
                          onChange={(e) => {
                            formik.setFieldValue(
                              `declaration_2`,
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label="I have now examined the processed stone and confirm that all processing specified above has been successfully completed without damage."
                    />
                  </div>
                  {formik.touched?.declaration_2 &&
                  formik?.errors?.declaration_2 ? (
                    <GlobalErrorMsgBox
                      errMessage={formik?.errors?.declaration_2}
                    />
                  ) : null}
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Name *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalTextField
                        type="text"
                        value={formik.values.declaration_2_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        id="declaration_2_name"
                        name="declaration_2_name"
                        error={
                          formik.touched.declaration_2_name &&
                          Boolean(formik.errors.declaration_2_name)
                        }
                        helperText={
                          formik.touched.declaration_2_name &&
                          formik.errors.declaration_2_name
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Date *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}}>
                      <GlobalDateField
                        value={
                          formik.values.declaration_2_date
                            ? dayjs(
                                formik.values.declaration_2_date,
                                "MM-DD-YYYY"
                              ).toDate()
                            : null
                        }
                        onChange={(newValue) => {
                          formik.setFieldValue(`declaration_2_date`, newValue);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched(`declaration_2_date`, true)
                        }
                        id="declaration_2_date"
                        name="declaration_2_date"
                        error={
                          formik.touched.declaration_2_date &&
                          Boolean(formik.errors.declaration_2_date)
                        }
                        helperText={
                          formik.touched.declaration_2_date &&
                          formik.errors.declaration_2_date
                        }
                        clearBtn={formik.values.declaration_2_date !== ""}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{xs: 12, md: 4, sm: 4, lg: 4, xl: 4}}>
                  <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 12}}>
                      <h3 className={style.htitleText}>Signature *</h3>
                    </Grid>
                    <Grid size={{xs: 12, md: 12}} className="choosefileBtn">
                      <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        startIcon={<CloudUploadIcon />}
                      >
                        {formik.values?.declaration_2_sign?.name ? (
                          <span>{formik.values?.declaration_2_sign?.name}</span>
                        ) : formik.values?.declaration_2_sign?.includes(
                            "https://"
                          ) ? (
                          <>
                            <span className={style.safeVFile}>
                              {
                                formik.values?.declaration_2_sign.split(
                                  "/images/"
                                )[1]
                              }
                            </span>
                          </>
                        ) : (
                          "Upload"
                        )}
                        <VisuallyHiddenInput
                          ref={declaration2SignRef}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                          onChange={(event) =>
                            handleFileUploadWithoutVids({
                              event,
                              fieldName: `declaration_2_sign`,
                              formik,
                              onError: (message) =>
                                GlobalNotificationPopup({
                                  type: "error",
                                  message,
                                }),
                            })
                          }
                        />
                      </Button>
                      {formik.touched?.declaration_2_sign &&
                      formik?.errors?.declaration_2_sign ? (
                        <GlobalErrorMsgBox
                          errMessage={formik?.errors?.declaration_2_sign}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  className={diamondStyle.SubBtn}
                  type="button"
                  onClick={handleReset}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  className={diamondStyle.SubBtn}
                  type="submit"
                  // disabled
                >
                  Submit
                </Button>
              </Grid>
              {/* main container close */}
            </form>
          </Box>
        </motion.div>
      </section>
    </>
  );
};

export default DiamondForm;
